import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

/** Catalog slug is `billing`; commercial name is ITaaS Invoice. */
export const INVOICE_HERO_PRODUCT_ASSET =
  "/assets/products/invoice/hero/itaas-invoice-product-dashboard.png";
export const INVOICE_HERO_HUMAN_ASSET =
  "/assets/products/invoice/hero/itaas-invoice-professional.png?v=1";

/** Actors grounded in Invoice SoT (customers, invoices, payments, quotes/credits). */
export const INVOICE_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "direction",
    icon: "🏛️",
    titleFr: "Direction",
    titleEn: "Leadership",
    bodyFr: "Suivez facturation, encaissements et montants dus depuis un tableau de bord unique.",
    bodyEn: "Track invoicing, collections and amounts due from one dashboard.",
  },
  {
    id: "finance",
    icon: "💼",
    titleFr: "Finance & comptabilité",
    titleEn: "Finance & accounting",
    bodyFr: "Émettez factures et avoirs, enregistrez les paiements et conservez un journal d’audit.",
    bodyEn: "Issue invoices and credit notes, record payments and keep an audit journal.",
  },
  {
    id: "sales",
    icon: "📋",
    titleFr: "Commercial",
    titleEn: "Sales",
    bodyFr: "Préparez devis et convertissez-les en factures sans ressaisie, avec un fichier clients centralisé.",
    bodyEn: "Prepare quotes and convert them to invoices without re-entry, with a centralized customer file.",
  },
  {
    id: "admin",
    icon: "🗂️",
    titleFr: "Administration",
    titleEn: "Administration",
    bodyFr: "Paramétrez devise et règles financières par organisation.",
    bodyEn: "Configure currency and financial settings per organization.",
  },
  {
    id: "clients",
    icon: "🧑‍💼",
    titleFr: "Clients facturés",
    titleEn: "Billed customers",
    bodyFr: "Accédez au portail client pour consulter factures et paiements selon le périmètre activé.",
    bodyEn: "Access the customer portal to view invoices and payments within the enabled scope.",
  },
];

/** Qualitative outcomes from catalog valuePropositions — no invented metrics. */
export const INVOICE_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "unify",
    icon: "🔗",
    titleFr: "Facturation unifiée",
    titleEn: "Unified invoicing",
    bodyFr: "Évitez de dupliquer la facturation dans chaque module métier de la Suite.",
    bodyEn: "Avoid duplicating invoicing inside every Suite business module.",
  },
  {
    id: "cash",
    icon: "💵",
    titleFr: "Encaissements suivis",
    titleEn: "Tracked collections",
    bodyFr: "Reliez factures, paiements et montants dus pour un suivi clair des recettes.",
    bodyEn: "Connect invoices, payments and amounts due for clear revenue follow-up.",
  },
  {
    id: "customers",
    icon: "👥",
    titleFr: "Clients centralisés",
    titleEn: "Centralized customers",
    bodyFr: "Conservez un fichier de clients facturables partagé, prêt pour devis et factures.",
    bodyEn: "Keep a shared billable-customer file, ready for quotes and invoices.",
  },
  {
    id: "suite",
    icon: "🧩",
    titleFr: "Pont Suite & verticaux",
    titleEn: "Suite & vertical bridge",
    bodyFr: "S’articule avec Finance, CRM et les portails métier (école, santé, mines) via événements.",
    bodyEn: "Connects with Finance, CRM and vertical portals (school, health, mining) via events.",
  },
];

/** Capability cards derived from catalog description + features (not roadmap). */
export const INVOICE_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Clients & documents",
    titleEn: "Customers & documents",
    features: [
      "Clients facturables centralisés",
      "Émission et suivi des factures",
      "Devis et avoirs",
    ],
  },
  {
    titleFr: "Paiements & suivi",
    titleEn: "Payments & follow-up",
    features: [
      "Enregistrement des paiements",
      "Suivi des montants dus",
      "Factures récurrentes",
      "Journal d’audit des opérations",
    ],
  },
  {
    titleFr: "Organisation",
    titleEn: "Organization",
    features: [
      "Facturation multi-organisation",
      "Paramètres financiers configurables",
      "Devise par défaut configurable",
    ],
  },
  {
    titleFr: "Suite & portail",
    titleEn: "Suite & portal",
    features: [
      "Portail client / espace client",
      "Rapports ventes et paiements",
      "Ingestion d’événements métier",
    ],
  },
];

const INVOICE_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "suite",
    labelFr: "Module Invoice de la Suite ITaaS",
    labelEn: "ITaaS Suite Invoice module",
  },
  {
    id: "scope",
    labelFr: "Facturation dédiée — Finance et CRM séparés",
    labelEn: "Dedicated invoicing — Finance and CRM stay separate",
  },
  {
    id: "local",
    labelFr: "Accompagnement ITaaS RDC",
    labelEn: "ITaaS RDC support",
  },
];

export const INVOICE_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: INVOICE_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS Invoice — factures, clients et encaissements",
    en: "ITaaS Invoice dashboard — invoices, customers and collections",
  },
  humanImageSrc: INVOICE_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Professionnelle consultant une facture et un smartphone",
    en: "Professional reviewing an invoice and a smartphone",
  },
  visualCaption: {
    fr: "Interface produit ITaaS Invoice — tableau de bord de facturation",
    en: "ITaaS Invoice product interface — billing dashboard",
  },
  heroKicker: {
    fr: "Factures · Devis · Avoirs · Clients · Paiements",
    en: "Invoices · Quotes · Credits · Customers · Payments",
  },
  audiences: INVOICE_AUDIENCES,
  audienceTitle: {
    fr: "Une facturation claire pour chaque acteur de l’entreprise",
    en: "Clear invoicing for every stakeholder in the business",
  },
  outcomes: INVOICE_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour enchaîner devis, factures et encaissements.",
    en: "Practical benefits for running quotes, invoices and collections in one flow.",
  },
  capabilityGroups: INVOICE_CAPABILITY_GROUPS,
  contextTitle: {
    fr: "Le moteur de facturation de la Suite",
    en: "The Suite’s invoicing engine",
  },
  contextLead: {
    fr: "Conçu pour centraliser la facturation multi-organisation sans dupliquer Finance ni CRM — sans affirmation réglementaire non démontrée.",
    en: "Built to centralize multi-organization invoicing without duplicating Finance or CRM — without undemonstrated regulatory claims.",
  },
  contextFallbackPoints: [
    {
      fr: "Clients, factures, devis, avoirs et paiements dans un module dédié",
      en: "Customers, invoices, quotes, credit notes and payments in one dedicated module",
    },
    {
      fr: "Évite la facturation dupliquée dans chaque vertical métier",
      en: "Avoids duplicated invoicing inside every vertical product",
    },
    {
      fr: "Pont vers Finance, CRM et portails Suite via événements",
      en: "Bridge to Finance, CRM and Suite portals via events",
    },
  ],
  trustBaseChips: INVOICE_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
};
